/**
 * useScrollRestoration, restore window scroll position when the user
 * navigates back to a page they've already scrolled on.
 *
 * Usage on the source page (LegislatorProfile, Bills index, etc.):
 *   const { saveScroll } = useScrollRestoration("legislator:" + id, isReady);
 *   // and call `saveScroll()` right before any outbound navigation that
 *   // pushes a new history entry, e.g. before navigate(`/bill/${id}`).
 *
 * Behaviour:
 *   - On mount AND once `isReady` becomes truthy (data loaded so the page
 *     has expanded to its full height), reads sessionStorage and restores
 *     window.scrollY to the saved value.
 *   - Saves are session-scoped and TTL'd to 5 minutes so a stale Y value
 *     from yesterday doesn't surprise the user.
 *
 * Why not React Router's <ScrollRestoration>? It requires the data-router
 * variant of RR v6 which we'd need to migrate the whole app to. This hook
 * is a 30-line drop-in that works with our existing BrowserRouter setup.
 */
import { useEffect, useRef, useCallback } from "react";

const TTL_MS = 5 * 60 * 1000; // 5 minutes
const KEY = (k) => `whip:scroll:${k}`;

export default function useScrollRestoration(storageKey, isReady = true) {
  const restoredRef = useRef(false);

  // Restore once data has loaded and the DOM is at full height.
  useEffect(() => {
    if (!isReady || restoredRef.current) return;
    try {
      const raw = sessionStorage.getItem(KEY(storageKey));
      if (!raw) return;
      const { y, t } = JSON.parse(raw);
      if (typeof y !== "number" || Date.now() - t > TTL_MS) {
        sessionStorage.removeItem(KEY(storageKey));
        return;
      }
      // Defer until layout settles, recharts and lazy imgs can shift content.
      requestAnimationFrame(() => window.scrollTo(0, y));
      restoredRef.current = true;
    } catch {
      // sessionStorage not available / quota, fail silent.
    }
  }, [isReady, storageKey]);

  const saveScroll = useCallback(() => {
    try {
      sessionStorage.setItem(
        KEY(storageKey),
        JSON.stringify({ y: window.scrollY, t: Date.now() })
      );
    } catch {
      // ignore
    }
  }, [storageKey]);

  return { saveScroll };
}
